<template>
  <div>
    <!-- 追加（書き込みできる人だけ） -->
    <div v-if="canEdit" class="add-box">
      <div class="flex flex-wrap items-end gap-x-4 gap-y-2">
        <!-- 区分は東京／千葉／埼玉の3つ。東京・千葉は雇用指示の最低賃金判定と同じ値。自由入力にすると合わなくなるので選ばせる -->
        <label class="field" v-tooltip="salesOfficeTip">
          <span>{{ salesOfficeCaption }}</span>
          <select v-model="add.salesOffice" class="input w-40">
            <option value="">選択してください</option>
            <option v-for="o in officeChoices" :key="o" :value="o">{{ o }}</option>
          </select>
        </label>
        <div class="field">
          <span>適用開始日</span>
          <WmsDateField v-model="add.startDate" name="addStartDate" class="w-40" :clearable="true" />
        </div>
        <label class="field">
          <span>最低賃金（時給）</span>
          <!-- ホイールで数値が動かないよう、ホイールが来たらフォーカスを外す -->
          <input v-model="add.minWage" type="number" min="1" step="1" class="input w-32 text-right" @wheel="$event.target.blur()" />
        </label>
        <PrimaryButton text="追加" class="w-24" :disabled="saving" @click="submitAdd">
          <Icon slot="before" iconName="Plus" iconType="outline" :strokeWidth="2" class="h-4 w-4" />
        </PrimaryButton>
      </div>
      <!-- 埼玉は賃金一括変更だけで使う区分。入力画面・代勤・欠員募集の最低賃金チェックは東京／千葉の行だけを見る -->
      <p class="note">埼玉は「賃金一括変更」で、{{ officeNote['埼玉'] }}に使います。雇用指示の{{ salesOfficeCaption }}は東京／千葉のままです。</p>
    </div>

    <div v-if="loading" class="empty-text">Loading...</div>
    <div v-else-if="!rows.length" class="empty-text">最低賃金の登録はありません。</div>
    <div v-else class="table-wrap">
      <table class="min-table">
        <thead>
          <tr>
            <th class="text-left" v-tooltip="salesOfficeTip">{{ salesOfficeCaption }}</th>
            <th>適用開始日</th>
            <th>適用終了日</th>
            <th>最低賃金</th>
            <th>状態</th>
            <th v-if="canEdit">操作</th>
          </tr>
        </thead>
        <tbody>
          <!-- 営業所が変わる行は上に濃い線。適用中の行は薄い藍色 -->
          <tr v-for="(row, ri) in rows" :key="row.id" :class="[rowClass(row), isOfficeChanged(ri) ? 'pref-top' : '']">
            <!-- 修正中の行は入力欄 -->
            <template v-if="edit.id === row.id">
              <td class="text-left">
                <select v-model="edit.salesOffice" class="input w-40">
                  <option value="">選択してください</option>
                  <option v-for="o in officeChoices" :key="o" :value="o">{{ o }}</option>
                </select>
              </td>
              <td><WmsDateField v-model="edit.startDate" name="editStartDate" class="w-32 mx-auto" /></td>
              <td class="sub">{{ slash(row.endDate) }}</td>
              <td><input v-model="edit.minWage" type="number" min="1" step="1" class="input w-24 text-center" @wheel="$event.target.blur()" /></td>
              <td><Badge :color="badgeColor(row)">{{ badgeText(row) }}</Badge></td>
              <td class="ops">
                <button type="button" class="btn-ghost primary" :disabled="saving" @click="submitEdit">
                  <Icon iconName="Save" iconType="outline" :strokeWidth="2" class="h-4 w-4" />保存
                </button>
                <button type="button" class="btn-ghost" :disabled="saving" @click="cancelEdit">
                  <Icon iconName="X" iconType="outline" :strokeWidth="2" class="h-4 w-4" />キャンセル
                </button>
              </td>
            </template>
            <template v-else>
              <td class="text-left font-bold">{{ row.salesOffice }}</td>
              <td>{{ slash(row.startDate) }}</td>
              <td>{{ slash(row.endDate) }}</td>
              <!-- 金額の下に、1つ前の期間からいくら上がったかを添える -->
              <td>
                <div class="money" :class="row.isCurrent || row.isFuture ? 'big' : ''">{{ yen(row.minWage) }}</div>
                <div v-if="diffText(row)" class="sub">{{ diffText(row) }}</div>
              </td>
              <td><Badge :color="badgeColor(row)">{{ badgeText(row) }}</Badge></td>
              <!-- 修正と削除は押し間違えないよう、ボタンにして間を空ける -->
              <td v-if="canEdit" class="ops">
                <button type="button" class="btn-ghost" :disabled="saving" @click="startEdit(row)">
                  <Icon iconName="Pencil" iconType="outline" :strokeWidth="2" class="h-4 w-4" />修正
                </button>
                <button type="button" class="btn-ghost danger" :disabled="saving" @click="$emit('delete', row)">
                  <Icon iconName="Trash" iconType="outline" :strokeWidth="2" class="h-4 w-4" />削除
                </button>
              </td>
            </template>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import Badge from '@components/Badge.vue'
import Icon from '@components/Icon.vue'
import PrimaryButton from '@components/PrimaryButton.vue'
import WmsDateField from '@wmscomponents/WmsDateField.vue'
import * as dialogs from '@libs/dialogs'
import * as moment from 'moment'
import { MIN_WAGE_OFFICE_SELECTIONS, MIN_WAGE_OFFICE_NOTE, SALES_OFFICE_CAPTION, SALES_OFFICE_TIP } from '@libs/constantsAKBS'

export default {
  components: {
    Badge,
    Icon,
    PrimaryButton,
    WmsDateField
  },

  props: {
    // 最低賃金マスタの行（id, salesOffice, startDate, endDate, minWage, isCurrent, isFuture, 登録・更新の日時と名前）
    rows: {
      type: Array,
      default: () => []
    },
    // マスタにある区分（親が持つ。入力の候補は固定の東京／千葉／埼玉を使うので、ここでは使わない）
    offices: {
      type: Array,
      default: () => []
    },
    // 追加・修正・削除ができるか
    canEdit: {
      type: Boolean,
      default: false
    },
    saving: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      // 区分の選択肢（東京／千葉／埼玉）。画面では「最低賃金判定」と出す
      officeChoices: MIN_WAGE_OFFICE_SELECTIONS,
      officeNote: MIN_WAGE_OFFICE_NOTE,
      salesOfficeCaption: SALES_OFFICE_CAPTION,
      salesOfficeTip: SALES_OFFICE_TIP,
      // 追加欄
      add: { salesOffice: '', startDate: '', minWage: '' },
      // 修正中の行（id が空なら修正していない）
      edit: { id: '', salesOffice: '', startDate: '', minWage: '' }
    }
  },

  methods: {
    /**
     * 追加欄の中身を確かめて親に渡す（保存できたときだけ追加欄を空にする。失敗したら入れた値を残す）
     */
    async submitAdd() {
      if (!await this.validate(this.add)) {
        return
      }
      this.$emit('save', { id: null, salesOffice: this.add.salesOffice, startDate: this.add.startDate, minWage: Number(this.add.minWage) }, ok => {
        if (ok) {
          this.add = { salesOffice: '', startDate: '', minWage: '' }
        }
      })
    },

    /**
     * 修正を始める（今の値を入力欄に写す）
     * @param {Object} row 行
     */
    startEdit(row) {
      this.edit = { id: row.id, salesOffice: row.salesOffice, startDate: row.startDate, minWage: row.minWage }
    },

    cancelEdit() {
      this.edit = { id: '', salesOffice: '', startDate: '', minWage: '' }
    },

    /**
     * 修正した中身を確かめて親に渡す（保存できたときだけ修正を終える。失敗したら入力欄のまま）
     */
    async submitEdit() {
      if (!await this.validate(this.edit)) {
        return
      }
      this.$emit('save', { id: this.edit.id, salesOffice: this.edit.salesOffice, startDate: this.edit.startDate, minWage: Number(this.edit.minWage) }, ok => {
        if (ok) {
          this.cancelEdit()
        }
      })
    },

    /**
     * 入力の確認（区分・適用開始日・1円以上の整数の最低賃金）
     * @param {Object} v 入力
     */
    async validate(v) {
      if (!String(v.salesOffice || '').trim()) {
        await dialogs.showErrorDialog(this.salesOfficeCaption, `${this.salesOfficeCaption}（東京／千葉／埼玉）を選んでください。`)
        return false
      }
      if (!v.startDate) {
        await dialogs.showErrorDialog('適用開始日', '適用開始日を入れてください。')
        return false
      }
      const wage = Number(v.minWage)
      if (!Number.isInteger(wage) || wage <= 0) {
        await dialogs.showErrorDialog('最低賃金', '最低賃金は 1 円以上の整数で入れてください。')
        return false
      }
      return true
    },

    /**
     * 行の色（適用中は薄い藍色、終了した行は薄い文字）
     */
    rowClass(row) {
      if (row.isCurrent) {
        return 'current'
      }
      return !row.isCurrent && !row.isFuture ? 'ended' : ''
    },

    /**
     * その行から営業所が変わるか（前の行と違う。先頭の行は変わらない扱い）
     * @param {Number} index 行の位置
     */
    isOfficeChanged(index) {
      return index > 0 && this.rows[index].salesOffice !== this.rows[index - 1].salesOffice
    },

    /**
     * 1つ前の期間からの差（1つ前の金額 prevMinWage は API が付けて返す。範囲で絞って前の行が表に無くても出せる）
     * 1つ前が無ければ空
     * @param {Object} row 行
     */
    diffText(row) {
      if (row.prevMinWage === null || row.prevMinWage === undefined || row.minWage === null) {
        return ''
      }
      const diff = Number(row.minWage) - Number(row.prevMinWage)
      if (diff === 0) {
        return '前回と同じ'
      }
      return `前回から ${diff > 0 ? '＋' : '−'}${Math.abs(diff).toLocaleString()}円`
    },

    badgeText(row) {
      return row.isCurrent ? '適用中' : (row.isFuture ? '予定' : '終了')
    },

    /**
     * バッジの色（適用中は緑。登録済み一覧の「在職」と同じ色。予定は青、終了は灰色）
     */
    badgeColor(row) {
      return row.isCurrent ? 'lightGreen' : (row.isFuture ? 'blue' : 'gray')
    },

    slash(day) {
      return day ? moment(day).format('YYYY/MM/DD') : ''
    },

    yen(val) {
      if (val === null || val === undefined || val === '') {
        return '-'
      }
      return `${Number(val).toLocaleString()}円`
    }
  }
}
</script>

<style scoped>
/* 追加欄（点線の枠。表と同じく画面の左右中央） */
.add-box {
  width: fit-content;
  max-width: 100%;
  margin: 0 auto 12px;
  border: 1px dashed #c7d2fe;
  border-radius: 6px;
  padding: 10px 12px;
  background: #eef2ff;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* 追加欄の下の説明 */
.note {
  margin-top: 8px;
  font-size: 0.75rem;
  color: #4b5563;
}

/* 項目名（WmsDateField の見出しと同じ太字・灰色） */
.field > span {
  font-size: 0.75rem;
  font-weight: bold;
  color: #374151;
}

.input {
  height: 36px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 0 8px;
  background: #fff;
  font-size: 0.875rem;
}

/* 操作の欄（修正・削除・やめる）。ボタンにして、押し間違えないよう間を空ける */
.ops {
  white-space: nowrap;
}

.ops > * + * {
  margin-left: 20px;
}

/* 保存・キャンセル・修正・削除は全部同じ幅（一番長い「キャンセル」に合わせる） */
.btn-ghost {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 112px;
  height: 30px;
  padding: 0 8px;
  border: 1px solid #c7d2fe;
  border-radius: 4px;
  background: #fff;
  color: #3730a3;
  font-size: 0.75rem;
  font-weight: bold;
}

.btn-ghost:hover {
  background: #eef2ff;
}

/* 保存は藍色で塗る（修正・削除と同じ大きさ） */
.btn-ghost.primary {
  border-color: #4f46e5;
  background: #4f46e5;
  color: #fff;
}

.btn-ghost.primary:hover {
  background: #4338ca;
}

.btn-ghost.danger {
  border-color: #fca5a5;
  color: #dc2626;
}

.btn-ghost.danger:hover {
  background: #fef2f2;
}

.btn-ghost:disabled {
  border-color: #e5e7eb;
  color: #9ca3af;
  background: #fff;
  cursor: not-allowed;
}

.empty-text {
  font-size: 0.875rem;
  color: #9ca3af;
}

/* 一覧（登録済み一覧の表と同じ角丸・藍色の見出し。画面の左右中央） */
.table-wrap {
  width: fit-content;
  max-width: 100%;
  margin: 0 auto;
  overflow-x: auto;
  border: 1px solid #c7d2fe;
  border-radius: 8px;
}

.min-table {
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.8rem;
  white-space: nowrap;
}

.min-table th,
.min-table td {
  padding: 6px 12px;
  text-align: center;
  vertical-align: middle;
  border-right: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
}

.min-table th:last-child,
.min-table td:last-child {
  border-right: none;
}

.min-table tbody tr:last-child td {
  border-bottom: none;
}

.min-table thead th {
  background: #e0e7ff;
  color: #3730a3;
  font-weight: bold;
  font-size: 0.75rem;
  border-bottom-color: #c7d2fe;
}

.min-table .text-left {
  text-align: left;
}

.min-table .sub {
  font-size: 0.7rem;
  color: #6b7280;
}

.min-table .money {
  font-weight: bold;
}

.min-table .money.big {
  font-size: 0.95rem;
  color: #1e3a8a;
}

.min-table tr.ended td {
  color: #9ca3af;
}

/* 適用中の行は薄い藍色 */
.min-table tr.current td {
  background: #eef2ff;
}

/* 都道府県が変わる行は上に藍色の太い線（同じ都道府県がひとかたまりに見える） */
.min-table tr.pref-top td {
  border-top: 3px solid #a5b4fc;
}
</style>
